import type { BoardVisit } from "@board/boardVisit";
import { type BoardCardGroup, groupBoardVisits } from "@board/groupBoardVisits";
import {
  DROPOFF_POSITIONS,
  EXAM_POSITIONS,
  type Lane,
  placeOnBoard,
  POSITION_LABELS,
  type Position,
  TERMINAL_POSITIONS,
} from "@board/positions";
import { cardTime, type PlacedVisit } from "@board/VisitCard";
import {
  BoardColumn,
  BoardLane,
  cn,
  Skeleton,
} from "@scribenote/brian-design-system";
import { useNow } from "@utils/use-now";
import { Fragment, type ReactNode, useMemo } from "react";

const LANES: { lane: Lane; title: string; positions: readonly Position[] }[] = [
  { lane: "exam", title: "Exam visits", positions: EXAM_POSITIONS },
  { lane: "dropoff", title: "Drop-offs", positions: DROPOFF_POSITIONS },
];

const columnKey = (lane: Lane, position: Position) => `${lane}:${position}`;

const startOf = (visit: BoardVisit): string =>
  visit.scheduledStart ?? visit.createdAt;

const byColumnOrder =
  (terminal: boolean, now: number) =>
  (a: PlacedVisit, b: PlacedVisit): number => {
    if (terminal)
      return (cardTime(b, now)?.at ?? "").localeCompare(
        cardTime(a, now)?.at ?? "",
      );
    return startOf(a.visit).localeCompare(startOf(b.visit));
  };

/** Each column's cards in order; a household in one column and room is one card (BRI-382). */
const useBoardColumns = <V extends BoardVisit>(
  visits: readonly V[],
  now: number,
): Map<string, BoardCardGroup<V>[]> =>
  useMemo(() => {
    const seen = new Set<string>();
    const columns = new Map<string, PlacedVisit<V>[]>();
    for (const visit of visits) {
      if (seen.has(visit.id)) continue;
      seen.add(visit.id);
      const placed = { visit, ...placeOnBoard(visit, now) };
      const key = columnKey(placed.lane, placed.position);
      columns.set(key, [...(columns.get(key) ?? []), placed]);
    }
    for (const [key, placed] of columns) {
      const position = key.split(":")[1] as Position;
      placed.sort(byColumnOrder(TERMINAL_POSITIONS.has(position), now));
    }
    return groupBoardVisits(columns);
  }, [visits, now]);

/** The board from its visits alone; each data source supplies the card. */
export const BoardLanes = <V extends BoardVisit>({
  visits,
  renderCard,
}: {
  visits: readonly V[];
  renderCard: (card: BoardCardGroup<V>, now: number) => ReactNode;
}) => {
  const now = useNow();
  const columns = useBoardColumns(visits, now);

  return (
    <>
      {LANES.map(({ lane, title, positions }) => (
        <BoardLane key={lane} title={title}>
          {positions.map((position) => {
            const cards = columns.get(columnKey(lane, position)) ?? [];
            return (
              <BoardColumn
                key={position}
                title={POSITION_LABELS[position]}
                count={cards.reduce((n, card) => n + card.visits.length, 0)}
              >
                {cards.map((card) => (
                  <Fragment key={card.visits[0].visit.id}>
                    {renderCard(card, now)}
                  </Fragment>
                ))}
              </BoardColumn>
            );
          })}
        </BoardLane>
      ))}
    </>
  );
};

export const BoardSkeleton = () => (
  <>
    {LANES.map(({ lane, positions }) => (
      <div key={lane} className="grid auto-cols-fr grid-flow-col gap-4">
        {positions.map((position) => (
          <Skeleton key={position} shape="sheet" className="h-24" />
        ))}
      </div>
    ))}
  </>
);

export const BoardFrame = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "flex min-w-0 flex-col gap-[22px] overflow-x-auto pt-1.5 [&>*]:min-w-[74rem]",
      className,
    )}
  >
    {children}
  </div>
);
